import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  ChevronLeft, RefreshCw, Package, TrendingUp, TrendingDown, Percent,
  BarChart3, Clock, Store, Calendar, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { useStoreMapping } from '@/hooks/useStoreMapping';

// Format utilities
const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'N/D';
  return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(value);
};

const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined) return 'N/D';
  return new Intl.NumberFormat('pt-PT', { 
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals 
  }).format(value);
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return 'N/D';
  return `${formatNumber(value, 1)}%`;
};

// Products Analysis Tab
const ProductsAnalysis = ({ store, date }) => {
  const [loading, setLoading] = useState(true);
  const [topSkus, setTopSkus] = useState([]);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/retail-analytics/top-skus/${store}/${date}`, {
          params: { limit: 50 }
        });
        setTopSkus(res.data.top_skus || []);
      } catch (err) {
        setError('Erro ao carregar produtos');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    if (store && date) loadData();
  }, [store, date]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }
  
  if (error) {
    return <div className="text-red-600 text-center py-8">{error}</div>;
  }
  
  return (
    <div className="space-y-6">
      {/* Top by Revenue */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            Top por Faturação
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead className="text-right">Unidades</TableHead>
                <TableHead className="text-right">Faturação</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topSkus.slice(0, 20).map((sku, idx) => (
                <TableRow key={sku._id}>
                  <TableCell className="font-medium text-slate-500">{idx + 1}</TableCell>
                  <TableCell className="font-mono">{sku._id}</TableCell>
                  <TableCell className="text-right">{formatNumber(sku.quantity)}</TableCell>
                  <TableCell className="text-right font-semibold">{formatCurrency(sku.revenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {topSkus.length === 0 && (
            <p className="text-center text-slate-500 py-8">Sem dados de produtos</p>
          )}
        </CardContent>
      </Card>
      
      {/* Top by Units */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" />
            Top por Unidades
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead className="text-right">Unidades</TableHead>
                <TableHead className="text-right">Faturação</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...topSkus].sort((a, b) => b.quantity - a.quantity).slice(0, 20).map((sku, idx) => (
                <TableRow key={sku._id}>
                  <TableCell className="font-medium text-slate-500">{idx + 1}</TableCell>
                  <TableCell className="font-mono">{sku._id}</TableCell>
                  <TableCell className="text-right font-semibold">{formatNumber(sku.quantity)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(sku.revenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// Hourly Analysis Tab
const HourlyAnalysis = ({ store, date }) => {
  const [loading, setLoading] = useState(true);
  const [hourlyData, setHourlyData] = useState([]);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/retail-analytics/hourly/${store}/${date}`);
        setHourlyData(res.data.hourly || []);
      } catch (err) {
        setError('Erro ao carregar dados horários');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    if (store && date) loadData();
  }, [store, date]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }
  
  if (error) {
    return <div className="text-red-600 text-center py-8">{error}</div>;
  }
  
  const maxSales = Math.max(...hourlyData.map(h => h.sales || 0), 1);
  const maxTx = Math.max(...hourlyData.map(h => h.transactions || 0), 1);
  
  // Fill all hours 0-23
  const allHours = [];
  for (let i = 0; i < 24; i++) {
    const hourStr = i.toString().padStart(2, '0');
    const existing = hourlyData.find(h => h._id === hourStr);
    allHours.push({
      hour: hourStr,
      sales: existing?.sales || 0,
      transactions: existing?.transactions || 0,
    });
  }
  
  return (
    <div className="space-y-6">
      {/* Hourly chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Vendas por Hora
          </CardTitle>
          <CardDescription>Distribuição de vendas ao longo do dia</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {allHours.map((hour) => {
              const salesPct = (hour.sales / maxSales) * 100;
              const isActive = hour.sales > 0;
              
              return (
                <div key={hour.hour} className="flex items-center gap-3">
                  <span className={`w-10 text-xs text-right ${isActive ? 'text-slate-600' : 'text-slate-300'}`}>
                    {hour.hour}:00
                  </span>
                  <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${isActive ? 'bg-gradient-to-r from-red-500 to-red-600' : ''}`}
                      style={{ width: `${salesPct}%` }}
                    />
                  </div>
                  <span className={`w-24 text-xs text-right ${isActive ? 'font-medium' : 'text-slate-300'}`}>
                    {formatCurrency(hour.sales)}
                  </span>
                  <span className={`w-16 text-xs text-right ${isActive ? 'text-slate-500' : 'text-slate-300'}`}>
                    {hour.transactions} tx
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      
      {/* Summary table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Resumo Horário</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Hora</TableHead>
                <TableHead className="text-right">Vendas</TableHead>
                <TableHead className="text-right">Transações</TableHead>
                <TableHead className="text-right">Ticket Médio</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allHours.filter(h => h.sales > 0).map((hour) => (
                <TableRow key={hour.hour}>
                  <TableCell className="font-medium">{hour.hour}:00</TableCell>
                  <TableCell className="text-right font-semibold">{formatCurrency(hour.sales)}</TableCell>
                  <TableCell className="text-right">{hour.transactions}</TableCell>
                  <TableCell className="text-right">
                    {hour.transactions > 0 ? formatCurrency(hour.sales / hour.transactions) : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {hourlyData.length === 0 && (
            <p className="text-center text-slate-500 py-8">Sem dados horários</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Returns Analysis Tab
const ReturnsAnalysis = ({ store, date, metrics }) => {
  if (!metrics) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }
  
  const returnRate = metrics.gross_sales > 0 
    ? (metrics.returns_value / metrics.gross_sales) * 100 
    : 0;
  
  const unitsReturnRate = metrics.units_sold > 0
    ? (metrics.units_returned / metrics.units_sold) * 100
    : 0;
  
  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-slate-500 uppercase">Valor Devolvido</p>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(metrics.returns_value)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-slate-500 uppercase">Unidades Devolvidas</p>
            <p className="text-2xl font-bold text-red-600">{formatNumber(metrics.units_returned)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-slate-500 uppercase">Nº NCVS</p>
            <p className="text-2xl font-bold">{formatNumber(metrics.return_documents)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-slate-500 uppercase">Taxa Devolução</p>
            <p className="text-2xl font-bold">{formatPercent(returnRate)}</p>
            <p className="text-xs text-slate-400">valor / vendas brutas</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Return rate analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-red-600" />
            Análise de Devoluções
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-slate-700">Taxa de Devolução (Valor)</p>
                <p className="text-xs text-slate-500">Devoluções / Vendas Brutas</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold">{formatPercent(returnRate)}</p>
                <p className="text-xs text-slate-500">{formatCurrency(metrics.returns_value)} / {formatCurrency(metrics.gross_sales)}</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-slate-700">Taxa de Devolução (Unidades)</p>
                <p className="text-xs text-slate-500">Unidades Devolvidas / Unidades Vendidas</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold">{formatPercent(unitsReturnRate)}</p>
                <p className="text-xs text-slate-500">{metrics.units_returned} / {metrics.units_sold}</p>
              </div>
            </div>
          </div>
          
          {metrics.return_documents === 0 && (
            <p className="text-center text-emerald-600 py-4 mt-4 bg-emerald-50 rounded">
              Sem devoluções neste período
            </p>
          )}
        </CardContent>
      </Card>
      
      {/* Link to documents */}
      <div className="flex justify-center">
        <Link to={`/lojas/vendas?type=NCVS&store=${store}&date=${date}`}>
          <Button variant="outline">
            Ver Documentos NCVS
            <ArrowUpRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

// Discounts Analysis Tab
const DiscountsAnalysis = ({ store, date, metrics }) => {
  if (!metrics) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }
  
  const discountRate = metrics.gross_sales > 0 
    ? (metrics.discounts_total / metrics.gross_sales) * 100 
    : 0;
  
  const avgDiscountPerTx = metrics.sales_documents > 0
    ? metrics.discounts_total / metrics.sales_documents
    : 0;
  
  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-slate-500 uppercase">Total Descontos</p>
            <p className="text-2xl font-bold text-amber-600">{formatCurrency(metrics.discounts_total || 0)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-slate-500 uppercase">Taxa Desconto</p>
            <p className="text-2xl font-bold">{formatPercent(discountRate)}</p>
            <p className="text-xs text-slate-400">sobre vendas brutas</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-slate-500 uppercase">Desconto Médio/Tx</p>
            <p className="text-2xl font-bold">{formatCurrency(avgDiscountPerTx)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-slate-500 uppercase">Vendas Líquidas</p>
            <p className="text-2xl font-bold text-emerald-600">{formatCurrency(metrics.net_sales)}</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Discount breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Percent className="w-5 h-5 text-amber-600" />
            Análise de Descontos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-slate-700">Vendas Brutas</p>
                <p className="text-xs text-slate-500">Antes de descontos</p>
              </div>
              <p className="text-xl font-bold">{formatCurrency(metrics.gross_sales)}</p>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-amber-700">Descontos Aplicados</p>
                <p className="text-xs text-amber-600">{formatPercent(discountRate)} das vendas brutas</p>
              </div>
              <p className="text-xl font-bold text-amber-700">-{formatCurrency(metrics.discounts_total || 0)}</p>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-red-700">Devoluções</p>
              </div>
              <p className="text-xl font-bold text-red-700">-{formatCurrency(metrics.returns_value)}</p>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg border-2 border-emerald-200">
              <div>
                <p className="text-sm font-medium text-emerald-700">Vendas Líquidas</p>
                <p className="text-xs text-emerald-600">Resultado final</p>
              </div>
              <p className="text-xl font-bold text-emerald-700">{formatCurrency(metrics.net_sales)}</p>
            </div>
          </div>
          
          {(metrics.discounts_total || 0) === 0 && (
            <p className="text-center text-slate-500 py-4 mt-4 bg-slate-50 rounded">
              Sem descontos neste período
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Payments Analysis Tab
const PaymentsAnalysis = ({ store, date, metrics }) => {
  if (!metrics) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }
  
  const totalPayments = metrics.net_cash + metrics.net_card + (metrics.net_other || 0);
  const cashPct = totalPayments > 0 ? (metrics.net_cash / totalPayments) * 100 : 0;
  const cardPct = totalPayments > 0 ? (metrics.net_card / totalPayments) * 100 : 0;
  const otherPct = totalPayments > 0 ? ((metrics.net_other || 0) / totalPayments) * 100 : 0;
  
  return (
    <div className="space-y-6">
      {/* Payment mix bar */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Mix de Pagamentos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-8 rounded-full overflow-hidden flex bg-slate-100 mb-4">
            {cashPct > 0 && (
              <div className="bg-emerald-500 flex items-center justify-center text-white text-xs font-medium"
                   style={{ width: `${cashPct}%` }}>
                {cashPct > 10 && `${formatNumber(cashPct, 0)}%`}
              </div>
            )}
            {cardPct > 0 && (
              <div className="bg-blue-500 flex items-center justify-center text-white text-xs font-medium"
                   style={{ width: `${cardPct}%` }}>
                {cardPct > 10 && `${formatNumber(cardPct, 0)}%`}
              </div>
            )}
            {otherPct > 0 && (
              <div className="bg-amber-500 flex items-center justify-center text-white text-xs font-medium"
                   style={{ width: `${otherPct}%` }}>
                {otherPct > 10 && `${formatNumber(otherPct, 0)}%`}
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="w-4 h-4 rounded-full bg-emerald-500 mx-auto mb-1" />
              <p className="text-sm font-medium">Numerário</p>
              <p className="text-xs text-slate-500">{formatPercent(cashPct)}</p>
            </div>
            <div>
              <div className="w-4 h-4 rounded-full bg-blue-500 mx-auto mb-1" />
              <p className="text-sm font-medium">Cartão</p>
              <p className="text-xs text-slate-500">{formatPercent(cardPct)}</p>
            </div>
            <div>
              <div className="w-4 h-4 rounded-full bg-amber-500 mx-auto mb-1" />
              <p className="text-sm font-medium">Outros</p>
              <p className="text-xs text-slate-500">{formatPercent(otherPct)}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Detailed breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Cash */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500" />
              Numerário
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Recebido</span>
                <span className="font-medium text-emerald-600">+{formatCurrency(metrics.cash_received)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Devolvido</span>
                <span className="font-medium text-red-600">-{formatCurrency(metrics.cash_refunded)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-sm font-medium">Líquido</span>
                <span className="font-bold">{formatCurrency(metrics.net_cash)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              Cartão
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Recebido</span>
                <span className="font-medium text-emerald-600">+{formatCurrency(metrics.card_received)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Devolvido</span>
                <span className="font-medium text-red-600">-{formatCurrency(metrics.card_refunded)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-sm font-medium">Líquido</span>
                <span className="font-bold">{formatCurrency(metrics.net_card)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Other */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-500" />
              Outros
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Recebido</span>
                <span className="font-medium text-emerald-600">+{formatCurrency(metrics.other_received || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Devolvido</span>
                <span className="font-medium text-red-600">-{formatCurrency(metrics.other_refunded || 0)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-sm font-medium">Líquido</span>
                <span className="font-bold">{formatCurrency(metrics.net_other || 0)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Total */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Pagamentos Líquidos</p>
              <p className="text-xs text-slate-400">Numerário + Cartão + Outros</p>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(totalPayments)}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main component
const RetailAnalises = () => {
  const { getStoreName } = useStoreMapping();
  const { user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Filters
  const [store, setStore] = useState(searchParams.get('store') || 'NORTESH');
  const [date, setDate] = useState(searchParams.get('date') || '2026-09-07');
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'produtos');
  
  // Data
  const [stores, setStores] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Load stores
  useEffect(() => {
    const loadStores = async () => {
      try {
        const res = await axios.get(`${API}/retail-analytics/stores`);
        setStores(res.data.stores || []);
      } catch {
        setStores(['NORTESH']);
      }
    };
    loadStores();
  }, []);
  
  // Load metrics
  useEffect(() => {
    const loadMetrics = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/retail-analytics/metrics/${store}/${date}`);
        setMetrics(res.data);
      } catch (err) {
        console.error(err);
        setMetrics(null);
      } finally {
        setLoading(false);
      }
    };
    
    if (store && date) loadMetrics();
  }, [store, date]);
  
  // Update URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (store !== 'NORTESH') params.set('store', store);
    if (date !== '2026-09-07') params.set('date', date);
    if (activeTab !== 'produtos') params.set('tab', activeTab);
    setSearchParams(params, { replace: true });
  }, [store, date, activeTab, setSearchParams]);
  
  return (
    <div className="space-y-6" data-testid="retail-analises">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Análises</h1>
          <p className="text-sm text-slate-500 mt-1">Análise detalhada de vendas</p>
        </div>
        
        <Link to="/lojas">
          <Button variant="outline" size="sm">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Dashboard
          </Button>
        </Link>
      </div>
      
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={store} onValueChange={setStore}>
          <SelectTrigger className="w-[150px]">
            <Store className="w-4 h-4 mr-2 text-slate-500" />
            <SelectValue placeholder="Loja" />
          </SelectTrigger>
          <SelectContent>
            {stores.map(s => (
              <SelectItem key={s} value={s}>{getStoreName(s)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-500" />
          <Input 
            type="date" 
            value={date} 
            onChange={(e) => setDate(e.target.value)}
            className="w-[150px]"
          />
        </div>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="produtos" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            <span className="hidden sm:inline">Produtos</span>
          </TabsTrigger>
          <TabsTrigger value="horario" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span className="hidden sm:inline">Horário</span>
          </TabsTrigger>
          <TabsTrigger value="devolucoes" className="flex items-center gap-2">
            <TrendingDown className="w-4 h-4" />
            <span className="hidden sm:inline">Devoluções</span>
          </TabsTrigger>
          <TabsTrigger value="descontos" className="flex items-center gap-2">
            <Percent className="w-4 h-4" />
            <span className="hidden sm:inline">Descontos</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="produtos" className="mt-6">
          <ProductsAnalysis store={store} date={date} />
        </TabsContent>
        
        <TabsContent value="horario" className="mt-6">
          <HourlyAnalysis store={store} date={date} />
        </TabsContent>
        
        <TabsContent value="devolucoes" className="mt-6">
          <ReturnsAnalysis store={store} date={date} metrics={metrics} />
        </TabsContent>
        
        <TabsContent value="descontos" className="mt-6">
          <DiscountsAnalysis store={store} date={date} metrics={metrics} />
        </TabsContent>
      </Tabs>
      
      {/* Payments section - always visible at bottom */}
      <div className="mt-8 pt-8 border-t">
        <h2 className="text-xl font-bold text-slate-900 mb-4">Meios de Pagamento</h2>
        <PaymentsAnalysis store={store} date={date} metrics={metrics} />
      </div>
    </div>
  );
};

export default RetailAnalises;
